import { Box, Flex, Heading, Text, useBoolean, useMediaQuery } from '@chakra-ui/react';
import Image from 'next/image';
import { useState } from 'react';
import { useInView } from 'react-intersection-observer';
import useTranslation from 'next-translate/useTranslation';
import { MotionImage } from '@/constants/motionConstants';
import { handleGTMClick } from '@/helpers/handleGTMClick';

interface Item {
  title: string;
  description?: string;
}

interface Props {
  items: Item[];
  ectools?: boolean;
  locasty?: boolean;
  ectohub?: boolean;
  reverse?: boolean;
}

interface CardSources {
  [key: number]: string;
}

const IMAGE_CARD_LOCASTY_SRCS: CardSources = {
  0: '/images/homepage/illustration10.png',
  1: '/images/homepage/illustration8.png',
  2: '/images/homepage/illustration9.png',
};
const IMAGE_CARD_ECTOOLS_SRCS: CardSources = {
  0: '/images/homepage/illustration5.png',
  1: '/images/homepage/illustration6.png',
  2: '/images/homepage/illustration7.png',
};
const IMAGE_CARD_ECTOHUB_SRCS: CardSources = {
  0: '/images/homepage/illustration11.png',
  1: '/images/homepage/illustration12.png',
  2: '/images/homepage/illustration13.png',
};
const IMAGE_CARD_SRCS: CardSources = {
  0: '/images/homepage/illustration2.png',
  1: '/images/homepage/illustration3.png',
  2: '/images/homepage/illustration4.png',
};
const ICON_CARD_LOCASTY_SRCS: CardSources = {
  0: '/images/multi-component-animated/icon4.svg',
  1: '/images/multi-component-animated/icon5.svg',
  2: '/images/multi-component-animated/icon7.svg',
};
const ICON_COLOR_CARD_LOCASTY_SRCS: CardSources = {
  0: '/images/multi-component-animated/icon4color.svg',
  1: '/images/multi-component-animated/icon5color.svg',
  2: '/images/multi-component-animated/icon7color.svg',
};
const ICON_CARD_ECTOOLS_SRCS: CardSources = {
  0: '/images/multi-component-animated/icon1.svg',
  1: '/images/multi-component-animated/icon6.svg',
  2: '/images/multi-component-animated/icon3.svg',
};
const ICON_COLOR_CARD_ECTOOLS_SRCS: CardSources = {
  0: '/images/multi-component-animated/icon1color.svg',
  1: '/images/multi-component-animated/icon6color.svg',
  2: '/images/multi-component-animated/icon3color.svg',
};
const ICON_CARD_ECTOHUB_SRCS: CardSources = {
  0: '/images/multi-component-animated/icon8.svg',
  1: '/images/multi-component-animated/icon9.svg',
  2: '/images/multi-component-animated/icon10.svg',
};
const ICON_COLOR_CARD_ECTOHUB_SRCS: CardSources = {
  0: '/images/multi-component-animated/icon8color.svg',
  1: '/images/multi-component-animated/icon9color.svg',
  2: '/images/multi-component-animated/icon10color.svg',
};
const ICON_CARD_SRCS: CardSources = {
  0: '/images/multi-component-animated/icon1.svg',
  1: '/images/multi-component-animated/icon2.svg',
  2: '/images/multi-component-animated/icon3.svg',
};
const ICON_COLOR_CARD_SRCS: CardSources = {
  0: '/images/multi-component-animated/icon1color.svg',
  1: '/images/multi-component-animated/icon2color.svg',
  2: '/images/multi-component-animated/icon3color.svg',
};

const CARD_TYPE_ID = {
  locasty: ['locasty_local_seo', 'locasty_scalability', 'locasty_data_intelligence'],
  ectools: ['ectools_project_organization', 'ectools_tasks', 'ectools_history'],
  ectohub: ['ectohub_seo_optimized', 'ectohub_customization', 'ectohub_integration'],
  none: ['how_dashboard', 'how_agile', 'how_simplify'],
};

function DesktopCard({
  type,
  item,
  index,
  IMAGE_PROVIDER,
  IMAGE_PROVIDER_ACTIVE,
  currentIndex,
  setCurrentIndex,
}: {
  type: 'locasty' | 'ectools' | 'ectohub' | 'none';
  item: Item;
  index: number;
  IMAGE_PROVIDER: CardSources;
  IMAGE_PROVIDER_ACTIVE: CardSources;
  currentIndex: number;
  setCurrentIndex: any;
}) {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <Flex
      id={CARD_TYPE_ID[type][index]}
      key={index}
      gap="32px"
      p="32px"
      alignItems="flex-start"
      direction="row"
      borderRadius={index === currentIndex ? '16px' : '0px'}
      bgColor={index === currentIndex ? 'gray.100' : 'transparent'}
      _hover={{ cursor: 'pointer' }}
      onClick={(e) => handleGTMClick(e, () => setCurrentIndex(index))}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        transition: 'color 1s ease',
      }}
    >
      <Image
        src={index === currentIndex || isHovered ? IMAGE_PROVIDER_ACTIVE[index] : IMAGE_PROVIDER[index]}
        alt="icon"
        width="48"
        height="48"
        style={{
          transition: 'transform 1s ease, opacity 1s ease',
          opacity: index === currentIndex || isHovered ? 1 : 0.8,
        }}
      />
      <Flex direction="column" gap="8px" alignItems="flex-start">
        <Heading
          as="h3"
          color={index === currentIndex || isHovered ? 'blue.600' : 'gray.600'}
          fontSize="24px"
          fontWeight="700"
          lineHeight="32px"
          style={{
            transition: 'color 1s ease',
          }}
        >
          {item.title}
        </Heading>
        <Text
          as="p"
          color={index === currentIndex || isHovered ? 'gray.600' : 'gray.500'}
          fontSize="14px"
          lineHeight="20px"
          style={{
            transition: 'color 1s ease',
          }}
        >
          {item.description}
        </Text>
      </Flex>
    </Flex>
  );
}

function StickyCard({
  item,
  index,
  IMAGE_CARD_PROVIDER,
  IMAGE_ICON_PROVIDER,
}: {
  item: Item;
  index: number;
  IMAGE_CARD_PROVIDER: CardSources;
  IMAGE_ICON_PROVIDER: CardSources;
}) {
  const topSpacing = (index + 1) * 5;

  return (
    <Flex
      key={index}
      position="sticky"
      top={`${topSpacing}px`}
      gap="32px"
      p="32px"
      alignItems="center"
      direction="column"
      bgColor="#F7FAFC"
    >
      <Box
        width="100%"
        height="100%"
        position="relative"
        overflow="hidden"
        borderRadius="16px"
        background="linear-gradient(188deg, rgba(237, 235, 255, 0.2) 0%, rgba(235, 248, 255, 0.2) 100%)"
      >
        <Image
          src={IMAGE_CARD_PROVIDER[index]}
          width={645}
          height={599}
          alt="pages"
          style={{ position: 'relative', zIndex: '3' }}
        />
        <Image src="/images/homepage/topology.svg" alt="topology" fill />
      </Box>
      <Image src={IMAGE_ICON_PROVIDER[index]} alt="icon" width={48} height={48} />
      <Flex direction="column" gap="8px" alignItems="center">
        <Heading as="h3" color="blue.600" fontSize="24px" fontWeight="700" lineHeight="32px">
          {item.title}
        </Heading>
        <Text as="p" color="gray.600" fontSize="14px" lineHeight="20px" textAlign="center">
          {item.description}
        </Text>
      </Flex>
    </Flex>
  );
}

export default function MultiComponentAnimated({ items, ectools, locasty, ectohub, reverse }: Props) {
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const { ref, inView } = useInView();
  const [isMobileScreen] = useMediaQuery('(max-width: 984px)');
  const [loading, setLoading] = useBoolean();

  const { t } = useTranslation('common');

  const slideUp = {
    hidden: { y: '100%', opacity: 0 },
    visible: { y: '0%', opacity: 1, transition: { duration: 1 } },
  };

  const handleWheel = (e: any) => {
    // if the value is positive, it is down. If it's negative, it's up
    if (loading) {
      return;
    }

    if (e.deltaY > 0) {
      if (currentIndex === 2) {
        return;
      }

      setLoading.on();
      setCurrentIndex(currentIndex + 1);

      setTimeout(() => {
        setLoading.off();
      }, 1000);
    } else {
      if (currentIndex === 0) {
        return;
      }

      setLoading.on();
      setCurrentIndex(currentIndex - 1);

      setTimeout(() => {
        setLoading.off();
      }, 1000);
    }
  };

  const unloadScrollBars = () => {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;

    window.onscroll = () => {
      window.scrollTo(scrollLeft, scrollTop);
    };
  };

  const reloadScrollBars = () => {
    window.onscroll = () => {
      /**/
    };
  };

  return (
    <Flex
      direction="column"
      maxWidth="1216px"
      margin="0 auto"
      width={{ base: '90%', lg: '100%' }}
      mb="32px"
      gap={{ base: '32px', lg: '72px' }}
      pt={{ base: '40px', lg: '72px' }}
      pb={{ base: '40px', lg: '72px' }}
    >
      <Heading
        as="h2"
        width="100%"
        fontSize={{ base: '36px', lg: '48px' }}
        lineHeight={{ base: '40px', lg: '48px' }}
        fontWeight="800"
        textAlign="left"
        color="blue.900"
        m="0 auto 32px 0"
        ml={{ base: '0', lg: '0' }}
      >
        {locasty
          ? t('homepage.howWeDo.title3')
          : /* eslint-disable */
          ectools
          ? t('homepage.howWeDo.title2')
          : ectohub
          ? t('homepage.howWeDo.title4')
          : t('homepage.howWeDo.title')}
      </Heading>
      <Flex
        gap="56px"
        direction={reverse ? { base: 'column', lg: 'row-reverse' } : { base: 'column', lg: 'row' }}
        align={'center'}
      >
        <Box
          display={{ base: 'none', lg: 'block' }}
          width={{ base: '100%', lg: '50%' }}
          height={{ base: '303px', lg: '598px' }}
          position="relative"
          overflow="hidden"
          background="linear-gradient(188deg, rgba(237, 235, 255, 0.2) 0%, rgba(235, 248, 255, 0.2) 100%)"
          borderRadius="16px"
          ref={ref}
          onMouseEnter={() => unloadScrollBars()}
          onMouseLeave={() => reloadScrollBars()}
          onWheel={handleWheel}
        >
          {locasty && (
            <MotionImage
              key={currentIndex}
              src={IMAGE_CARD_LOCASTY_SRCS[currentIndex]}
              alt="pages"
              style={{ position: 'absolute', bottom: '0', zIndex: '3' }}
              variants={slideUp}
              initial="hidden"
              animate={inView ? 'visible' : 'hidden'}
              width="645"
              height="599"
            />
          )}
          {ectools && (
            <MotionImage
              key={currentIndex}
              src={IMAGE_CARD_ECTOOLS_SRCS[currentIndex]}
              alt="pages"
              style={{ position: 'absolute', bottom: '0', zIndex: '3' }}
              variants={slideUp}
              initial="hidden"
              animate={inView ? 'visible' : 'hidden'}
              width="645"
              height="599"
            />
          )}
          {ectohub && (
            <MotionImage
              key={currentIndex}
              src={IMAGE_CARD_ECTOHUB_SRCS[currentIndex]}
              alt="pages"
              style={{ position: 'absolute', bottom: '0', zIndex: '3' }}
              variants={slideUp}
              initial="hidden"
              animate={inView ? 'visible' : 'hidden'}
              width="645"
              height="599"
            />
          )}
          {Boolean(!ectools && !locasty && !ectohub) && (
            <MotionImage
              key={currentIndex}
              src={IMAGE_CARD_SRCS[currentIndex]}
              alt="pages"
              style={{ position: 'absolute', bottom: '0', zIndex: '3' }}
              variants={slideUp}
              initial="hidden"
              animate={inView ? 'visible' : 'hidden'}
              width="645"
              height="599"
            />
          )}

          <Image src="/images/homepage/topology.svg" alt="topology" fill />
        </Box>
        <Flex direction="column" flex="1" justifyContent="space-between">
          {!isMobileScreen && (
            <>
              {locasty &&
                items.map((item: Item, index: number) => (
                  <DesktopCard
                    type="locasty"
                    key={index}
                    item={item}
                    index={index}
                    IMAGE_PROVIDER={ICON_CARD_LOCASTY_SRCS}
                    IMAGE_PROVIDER_ACTIVE={ICON_COLOR_CARD_LOCASTY_SRCS}
                    currentIndex={currentIndex}
                    setCurrentIndex={setCurrentIndex}
                  />
                ))}
              {ectools &&
                items.map((item: Item, index: number) => (
                  <DesktopCard
                    type="ectools"
                    key={index}
                    item={item}
                    index={index}
                    IMAGE_PROVIDER={ICON_CARD_ECTOOLS_SRCS}
                    IMAGE_PROVIDER_ACTIVE={ICON_COLOR_CARD_ECTOOLS_SRCS}
                    currentIndex={currentIndex}
                    setCurrentIndex={setCurrentIndex}
                  />
                ))}
              {ectohub &&
                items.map((item: Item, index: number) => (
                  <DesktopCard
                    type="ectohub"
                    key={index}
                    item={item}
                    index={index}
                    IMAGE_PROVIDER={ICON_CARD_ECTOHUB_SRCS}
                    IMAGE_PROVIDER_ACTIVE={ICON_COLOR_CARD_ECTOHUB_SRCS}
                    currentIndex={currentIndex}
                    setCurrentIndex={setCurrentIndex}
                  />
                ))}
              {Boolean(!ectools && !locasty && !ectohub) &&
                items.map((item: Item, index: number) => (
                  <DesktopCard
                    type="none"
                    key={index}
                    item={item}
                    index={index}
                    IMAGE_PROVIDER={ICON_CARD_SRCS}
                    IMAGE_PROVIDER_ACTIVE={ICON_COLOR_CARD_SRCS}
                    currentIndex={currentIndex}
                    setCurrentIndex={setCurrentIndex}
                  />
                ))}
            </>
          )}

          {isMobileScreen && (
            <Box
              height="600px"
              m="0 auto"
              overflowY="auto"
              position="relative"
              sx={{
                '&::-webkit-scrollbar': {
                  display: 'none',
                },
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
              }}
            >
              {locasty &&
                items.map((item: Item, index: number) => (
                  <StickyCard
                    key={index}
                    item={item}
                    index={index}
                    IMAGE_CARD_PROVIDER={IMAGE_CARD_LOCASTY_SRCS}
                    IMAGE_ICON_PROVIDER={ICON_CARD_LOCASTY_SRCS}
                  />
                ))}
              {ectools &&
                items.map((item: Item, index: number) => (
                  <StickyCard
                    key={index}
                    item={item}
                    index={index}
                    IMAGE_CARD_PROVIDER={IMAGE_CARD_ECTOOLS_SRCS}
                    IMAGE_ICON_PROVIDER={ICON_CARD_ECTOOLS_SRCS}
                  />
                ))}
              {Boolean(!locasty && !ectools) &&
                items.map((item: Item, index: number) => (
                  <StickyCard
                    key={index}
                    item={item}
                    index={index}
                    IMAGE_CARD_PROVIDER={IMAGE_CARD_SRCS}
                    IMAGE_ICON_PROVIDER={ICON_CARD_SRCS}
                  />
                ))}
            </Box>
          )}
        </Flex>
      </Flex>
    </Flex>
  );
}
